import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setError(""); setLoading(true);
    try {
      // Strip any stray whitespace email clients may inject around the token
      const cleanToken = (token || "").trim().replace(/\s+/g, "");
      await axios.post(`${API}/auth/reset-password`, { token: cleanToken, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/auth?tab=login"), 2200);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not reset password. Please request a new link.");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#120021", display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 16px 40px" }}>
      <div className="glass-card animate-fadeInUp" style={{ maxWidth: "440px", width: "100%", padding: "32px", border: "1px solid rgba(255,209,102,0.2)" }}>
        <Link to="/auth?tab=login" style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "rgba(255,255,255,0.5)", fontSize: "0.82rem", textDecoration: "none", marginBottom: "14px" }}>
          <ArrowLeft size={14} /> Back to login
        </Link>
        <h1 className="font-cinzel gradient-text-gold" style={{ fontSize: "1.6rem", marginBottom: "8px" }}>Reset Password</h1>
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem", marginBottom: "22px" }}>
          Choose a new password for your AstroFate account. Minimum 8 characters.
        </p>

        {!token && (
          <div style={{ color: "#fca5a5", background: "rgba(239,68,68,0.1)", padding: "12px 14px", borderRadius: "10px", fontSize: "0.86rem" }}>
            Missing reset token. Please use the link from your email or <Link to="/auth/forgot" style={{ color: "#FFD166" }}>request a new one</Link>.
          </div>
        )}

        {token && done && (
          <div data-testid="reset-done" style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: "12px", padding: "16px", color: "#86efac" }}>
            ✓ Password updated. Redirecting to login…
          </div>
        )}

        {token && !done && (
          <form onSubmit={submit}>
            {[
              { label: "New Password", val: password, set: setPassword, testid: "reset-password-input" },
              { label: "Confirm Password", val: confirm, set: setConfirm, testid: "reset-confirm-input" },
            ].map(f => (
              <div key={f.testid} style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", color: "rgba(255,255,255,0.55)", fontSize: "0.78rem", marginBottom: "6px" }}>{f.label}</label>
                <div style={{ position: "relative" }}>
                  <Lock size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)" }} />
                  <input data-testid={f.testid} type={show ? "text" : "password"} required value={f.val} onChange={e => f.set(e.target.value)}
                    className="cosmic-input" style={{ paddingLeft: "34px", paddingRight: "38px" }} placeholder="••••••••" />
                  <button type="button" onClick={() => setShow(s => !s)} style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(255,255,255,0.45)", cursor: "pointer" }}>
                    {show ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            ))}
            {error && (
              <div style={{ color: "#fca5a5", fontSize: "0.82rem", background: "rgba(239,68,68,0.1)", padding: "10px 12px", borderRadius: "8px", marginBottom: "14px" }}>
                <div data-testid="reset-error">{error}</div>
                {/invalid|expired|already been used/i.test(error) && (
                  <div style={{ marginTop: "6px" }}>
                    <Link to="/auth/forgot" style={{ color: "#FFD166", textDecoration: "underline" }}>Request a new reset link →</Link>
                  </div>
                )}
              </div>
            )}
            <button data-testid="reset-submit-btn" type="submit" disabled={loading || !password || !confirm} className="btn-gold" style={{ width: "100%", opacity: (loading || !password || !confirm) ? 0.6 : 1 }}>
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
